// Metadatos compartidos por las vistas (se cargan una vez por montaje): SherpaEntity disponibles
// con sus campos, formularios activos con sus alias y campos de sistema de la respuesta.
import { computed, reactive } from 'vue';
import { listEntities, listForms } from '@vem/api/materializeApi.js';

export const catalog = reactive({
  entities: [],
  forms: [],
  systemAliases: [],
  loaded: false,
  loading: false,
  error: null,
});

export async function loadCatalog(force = false) {
  if ((catalog.loaded && !force) || catalog.loading) return;
  catalog.loading = true;
  catalog.error = null;
  try {
    const [entities, forms] = await Promise.all([listEntities(), listForms()]);
    catalog.entities = entities;
    catalog.forms = forms.forms;
    catalog.systemAliases = forms.systemAliases;
    catalog.loaded = true;
  } catch (e) {
    catalog.error = e.message;
  } finally {
    catalog.loading = false;
  }
}

export function resetCatalog() {
  Object.assign(catalog, { entities: [], forms: [], systemAliases: [], loaded: false, error: null });
}

export const entityByClass = (cls) => catalog.entities.find((e) => e.className === cls) || null;
export const formById = (id) => catalog.forms.find((f) => f.id === id) || null;
export const shortName = (cls) => (cls ? cls.substring(cls.lastIndexOf('.') + 1) : '');

/** Extensiones 1:1 registradas de una raíz: van siempre con ella. */
export const extensionsOf = (root) => catalog.entities
  .filter((e) => e.role === 'extension' && e.rootClass === root).map((e) => e.className);
/** «Market + MarketMedical»: la raíz con sus extensiones. */
export const comboName = (root) => [root, ...extensionsOf(root)].map((c) => entityByClass(c)?.name || shortName(c)).join(' + ');
/** «market · market_medical». */
export const comboTables = (root) => [root, ...extensionsOf(root)].map((c) => entityByClass(c)?.table).filter(Boolean).join(' · ');

/** Papel de un campo en la clave de la raíz (lo declara su registro): 'id' | 'origin' | null. */
export function keyRoleOf(root, cls, field) {
  const e = cls && cls === root ? entityByClass(cls) : null;
  if (!e) return null;
  return field === e.sourceIdField ? 'id' : field === e.originField ? 'origin' : null;
}

/** Alias de cada formulario activo: id → Set(alias). */
export const formAliasSets = computed(() => new Map(catalog.forms.map((f) => [f.id, new Set(f.aliases.map((a) => a.alias))])));

/** Nombre del formulario; si otro formulario activo se llama igual, con su número: «Accounts (441)». */
export function formName(questId) {
  const f = formById(questId);
  if (!f) return questId;
  const twin = catalog.forms.some((o) => o.id !== f.id && o.name === f.name);
  return twin ? `${f.name} (${Number(questId.replace(/\D/g, ''))})` : f.name;
}

/** Etiqueta de un alias: la del primero de `questIds` que lo trae; si no, la de cualquier formulario; si no, null. */
export function aliasLabel(alias, questIds = []) {
  const labelIn = (f) => {
    const a = f && f.aliases.find((x) => x.alias === alias);
    return a && a.label && a.label !== alias ? a.label : null;
  };
  for (const id of questIds) {
    const l = labelIn(formById(id));
    if (l) return l;
  }
  for (const f of catalog.forms) {
    const l = labelIn(f);
    if (l) return l;
  }
  return null;
}
